import { useQuery } from '@tanstack/react-query'

import type { WorkflowSearchTracker } from '@/lib/order-workflow-contract'
import type { ProductLot } from '@/lib/api/product-lot-api'
import type {
    StockOutItemInput,
    StockOutMaterialItemInput,
    StockOutMaterialSuggestion,
    StockOutPackagingLot,
    StockOutSuggestionItem,
} from '@/lib/api/sales-order-api'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import { OrderLotPicker } from '@/components/product-lots/OrderLotPicker'
import { datesLine } from '@/components/product-lots/product-lot-option'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

// Editor chọn lô dùng chung cho (a) popup "Tạo phiếu xuất kho" của đơn và
// (b) POS — thu ngân chọn/quét lô ngay tại quầy trước khi bán. Toàn bộ state
// (lines / matLines) do caller giữ; component chỉ render + validate.

export interface LotRow {
    lotId: string
    qty: string
    lot?: ProductLot
}
export interface LotLine {
    /** '' khi chưa có đơn (POS preview) — caller map theo productId. */
    orderItemId: string
    productId: string
    productName: string
    unit: string
    quantity: number
    rows: LotRow[]
}

// Lô vật tư đã chuẩn hóa (khớp giữa suggestion entity.PackagingLot và DTO
// /packaging-lots): id + mã lô + tồn khả dụng.
export interface MatLot {
    id: string
    code: string
    available: number
}
export interface MatRow {
    lotId: string
    qty: string
    lot?: MatLot
}
export interface MatLine {
    materialId: string
    name: string
    sku: string
    unit: string
    trackingMode: 'lot' | 'quantity'
    needed: number
    rows: MatRow[]
}

const EPS = 1e-6

function toMatLot(l: StockOutPackagingLot): MatLot {
    return { id: l.id, code: l.code, available: l.quantity_available }
}

/** Dựng dòng SP từ gợi ý: lô FIFO pre-fill (nếu có), else 1 row trống. */
export function seedLotLine(it: StockOutSuggestionItem): LotLine {
    return {
        orderItemId: it.order_item_id,
        productId: it.product_id,
        productName: it.product_name,
        unit: it.unit,
        quantity: it.quantity,
        rows:
            it.allocations.length > 0
                ? it.allocations.map((a) => ({
                      lotId: a.product_lot.id,
                      qty: String(a.quantity),
                      lot: a.product_lot,
                  }))
                : [{ lotId: '', qty: '' }],
    }
}

/**
 * Dựng 1 dòng vật tư từ gợi ý: lô FIFO pre-fill (nếu có), else 1 row trống.
 * Qty-mode cũng seed từ allocations (lô ảo STOCK) nhưng UI ẩn picker.
 */
export function seedMatLine(mt: StockOutMaterialSuggestion): MatLine {
    const rows: MatRow[] =
        mt.allocations.length > 0
            ? mt.allocations.map((a) => ({
                  lotId: a.packaging_lot.id,
                  qty: String(a.quantity),
                  lot: toMatLot(a.packaging_lot),
              }))
            : [{ lotId: '', qty: '' }]
    return {
        materialId: mt.packaging_material_id,
        name: mt.material_name,
        sku: mt.material_sku,
        unit: mt.unit,
        trackingMode: mt.tracking_mode,
        needed: mt.needed_qty,
        rows,
    }
}

// MaterialLotPicker — dropdown chọn lô cho 1 vật tư (lot-mode). Fetch lô còn tồn
// theo material (endpoint /packaging-lots trả code/quantity_available) → MatLot.
// seedLot đảm bảo lô đang chọn (từ suggestion) luôn hiện dù query chưa trả.
function MaterialLotPicker({
    materialId,
    value,
    seedLot,
    excludeLotIds,
    onChange,
}: {
    materialId: string
    value: string
    seedLot?: MatLot
    // Lô đã chọn ở CÁC DÒNG KHÁC của cùng vật tư → loại khỏi dropdown để không
    // chọn trùng (tránh phân bổ vượt tồn — BE cũng chặn nhưng chặn sớm ở FE).
    excludeLotIds: string[]
    onChange: (lot: MatLot) => void
}) {
    const { data } = useQuery({
        queryKey: ['packaging-lots-picker', materialId],
        queryFn: async () => {
            const res = await packagingLotApi.listLots({
                material_id: materialId,
                per_page: 1000,
                only_available: '1',
            })
            return res.data ?? []
        },
        enabled: Boolean(materialId),
    })
    const fetched: MatLot[] = (data ?? [])
        .filter((l) => l.quantity_available > 0)
        .map((l) => ({
            id: l.id,
            code: l.code,
            available: l.quantity_available,
        }))
    const withSeed =
        seedLot && !fetched.some((l) => l.id === seedLot.id)
            ? [seedLot, ...fetched]
            : fetched
    // Luôn giữ lô đang chọn của DÒNG NÀY (value); chỉ loại lô của dòng khác.
    const exclude = new Set(excludeLotIds.filter((id) => id !== value))
    const lots = withSeed.filter((l) => !exclude.has(l.id))
    return (
        <Select
            value={value || undefined}
            onValueChange={(id) => {
                const lot = lots.find((l) => l.id === id)
                if (lot) onChange(lot)
            }}
        >
            <SelectTrigger className="min-w-[200px]">
                <SelectValue
                    placeholder={m.stock_out_material_lot_placeholder()}
                />
            </SelectTrigger>
            <SelectContent>
                {lots.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                        {l.code} ·{' '}
                        {m.stock_out_material_lot_available_short({
                            qty: formatQty(l.available),
                        })}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    )
}

function matRowError(row: MatRow, unit: string): string | null {
    if (!row.lotId) return m.stock_out_lot_required()
    const qty = Number(row.qty)
    if (!row.qty || Number.isNaN(qty) || qty <= 0)
        return m.stock_out_qty_positive()
    if (row.lot && qty > row.lot.available + EPS) {
        return m.stock_out_lot_insufficient({
            available: formatQty(row.lot.available),
            unit,
            needed: formatQty(qty),
        })
    }
    return null
}
function matAllocated(line: MatLine): number {
    return line.rows.reduce((s, r) => {
        const q = Number(r.qty)
        return Number.isNaN(q) ? s : s + q
    }, 0)
}
function matMismatch(line: MatLine): string | null {
    if (Math.abs(matAllocated(line) - line.needed) > EPS) {
        return m.stock_out_line_mismatch({
            allocated: formatQty(matAllocated(line)),
            total: formatQty(line.needed),
            unit: line.unit,
        })
    }
    return null
}
export function hasMatError(lines: MatLine[]): boolean {
    return lines.some(
        (line) =>
            matMismatch(line) != null ||
            line.rows.some((r) => matRowError(r, line.unit) != null),
    )
}

// Thông tin lô đã chọn (dưới ô nhập): vị trí · tồn khả dụng · NSX/HSD.
// Giúp người soạn kho nhận diện đúng lô mà không phải mở lại dropdown.
function lotInfoLine(lot: ProductLot, unit: string): string {
    const parts: string[] = []
    // `||` (không phải `??`): position_code có thể là "" → vẫn phải rơi về name.
    const pos = lot.position_code || lot.position_name
    parts.push(
        pos
            ? m.stock_out_lot_position({ code: pos })
            : m.stock_out_lot_no_position(),
    )
    parts.push(
        m.stock_out_lot_available({
            qty: formatQty(lot.quantity_available),
            // Tồn là của LÔ → đơn vị lấy từ lô cho khớp dropdown; fallback đơn
            // vị dòng đơn khi lô thiếu product_unit.
            unit: lot.product_unit || unit,
        }),
    )
    const dates = datesLine(lot.manufactured_date, lot.expiry_date)
    if (dates) parts.push(dates)
    return parts.join(' · ')
}

// Lỗi từng dòng lô (đỏ dưới ô chọn lô) — thứ tự khớp line.rows.
function rowError(row: LotRow, unit: string): string | null {
    if (!row.lotId) return m.stock_out_lot_required()
    const qty = Number(row.qty)
    if (!row.qty || Number.isNaN(qty) || qty <= 0)
        return m.stock_out_qty_positive()
    if (row.lot && qty > row.lot.quantity_available + EPS) {
        return m.stock_out_lot_insufficient({
            available: formatQty(row.lot.quantity_available),
            unit,
            needed: formatQty(qty),
        })
    }
    return null
}

function allocatedOf(line: LotLine): number {
    return line.rows.reduce((sum, r) => {
        const q = Number(r.qty)
        return Number.isNaN(q) ? sum : sum + q
    }, 0)
}

/**
 * Phần THIẾU của một dòng: số lượng đơn trừ đi tổng phân bổ lô thật.
 *
 * Chỉ tính chiều thiếu. Phủ THỪA trả 0 vì thừa luôn là lỗi — bán nợ lô nới cho
 * kho hết hàng, không nới cho gõ nhầm số làm trừ kho nhiều hơn số bán.
 */
export function lineShortfall(line: LotLine): number {
    const short = line.quantity - allocatedOf(line)
    return short > EPS ? short : 0
}

// Lỗi mức dòng sản phẩm (∑ ≠ SL đơn).
//
// allowShortfall = thu ngân đã xác nhận bán nợ lô: lúc đó phủ THIẾU không còn là
// lỗi mà thành khoản nợ, nhưng phủ THỪA vẫn lỗi như cũ.
function lineMismatch(line: LotLine, allowShortfall = false): string | null {
    const allocated = allocatedOf(line)
    const delta = allocated - line.quantity
    if (allowShortfall && delta < -EPS) {
        return null
    }
    if (Math.abs(delta) > EPS) {
        return m.stock_out_line_mismatch({
            allocated: formatQty(allocated),
            total: formatQty(line.quantity),
            unit: line.unit,
        })
    }
    return null
}

/** Ô hoàn toàn trống: chưa chọn lô VÀ chưa nhập số. */
function isBlankRow(row: LotRow): boolean {
    return !row.lotId && !String(row.qty).trim()
}

/**
 * allowShortfall chỉ tha cho việc phủ thiếu. Mọi lỗi ô KHÁC vẫn giữ nguyên: đã
 * chọn lô mà bỏ trống số, đã nhập số mà chưa chọn lô, hay lấy quá tồn của chính
 * lô đó đều là lỗi dù có bán nợ hay không.
 *
 * Ngoại lệ duy nhất là Ô TRỐNG HOÀN TOÀN. Sản phẩm hết sạch hàng được
 * seedLotLine dựng sẵn một ô trống chờ; nếu vẫn tính nó là lỗi thì thu ngân tick
 * xác nhận xong vẫn không bán được — đúng bế tắc mà FD-372 sinh ra để gỡ.
 */
export function hasAnyError(lines: LotLine[], allowShortfall = false): boolean {
    return lines.some(
        (line) =>
            lineMismatch(line, allowShortfall) != null ||
            line.rows.some(
                (r) =>
                    !(allowShortfall && isBlankRow(r)) &&
                    rowError(r, line.unit) != null,
            ),
    )
}

/** Payload xuất kho theo đơn (order_item_id). */
export function linesToStockOutItems(lines: LotLine[]): StockOutItemInput[] {
    return lines.map((line) => ({
        order_item_id: line.orderItemId,
        allocations: line.rows.map((r) => ({
            product_lot_id: r.lotId,
            quantity: Number(r.qty),
        })),
    }))
}

export function matLinesToInputs(
    matLines: MatLine[],
): StockOutMaterialItemInput[] {
    return matLines.map((line) => ({
        packaging_material_id: line.materialId,
        allocations: line.rows.map((r) => ({
            packaging_lot_id: r.lotId,
            quantity: Number(r.qty),
        })),
    }))
}

interface LotAllocationEditorProps {
    workflow?: WorkflowSearchTracker
    lines: LotLine[]
    matLines: MatLine[]
    warehouseId?: string
    onLinesChange: (next: LotLine[]) => void
    onMatLinesChange: (next: MatLine[]) => void
    /** Nhỏ gọn cho panel POS (bớt padding, không tiêu đề section vật tư to). */
    compact?: boolean
    /**
     * Thu ngân đã xác nhận bán nợ lô: dòng phủ thiếu hiện nhãn "thiếu N" màu
     * cảnh báo thay vì báo đỏ lệch số (FD-372).
     */
    allowShortfall?: boolean
}

/**
 * Render các dòng SP (chọn lô + SL) và dòng vật tư (lot-mode có picker,
 * qty-mode chỉ báo). Không có nút submit — caller quyết định khi nào gửi.
 */
export function LotAllocationEditor({
    lines,
    matLines,
    warehouseId,
    onLinesChange,
    onMatLinesChange,
    compact,
    allowShortfall = false,
    workflow,
}: LotAllocationEditorProps) {
    const patchRow = (li: number, ri: number, patch: Partial<LotRow>) => {
        onLinesChange(
            lines.map((line, i) =>
                i !== li
                    ? line
                    : {
                          ...line,
                          rows: line.rows.map((row, j) =>
                              j !== ri ? row : { ...row, ...patch },
                          ),
                      },
            ),
        )
    }
    const addRow = (li: number) => {
        onLinesChange(
            lines.map((line, i) =>
                i !== li
                    ? line
                    : { ...line, rows: [...line.rows, { lotId: '', qty: '' }] },
            ),
        )
    }
    const removeRow = (li: number, ri: number) => {
        onLinesChange(
            lines.map((line, i) =>
                i !== li
                    ? line
                    : { ...line, rows: line.rows.filter((_, j) => j !== ri) },
            ),
        )
    }

    const patchMatRow = (li: number, ri: number, patch: Partial<MatRow>) => {
        onMatLinesChange(
            matLines.map((line, i) =>
                i !== li
                    ? line
                    : {
                          ...line,
                          rows: line.rows.map((row, j) =>
                              j !== ri ? row : { ...row, ...patch },
                          ),
                      },
            ),
        )
    }
    const addMatRow = (li: number) => {
        onMatLinesChange(
            matLines.map((line, i) =>
                i !== li
                    ? line
                    : { ...line, rows: [...line.rows, { lotId: '', qty: '' }] },
            ),
        )
    }
    const removeMatRow = (li: number, ri: number) => {
        onMatLinesChange(
            matLines.map((line, i) =>
                i !== li
                    ? line
                    : { ...line, rows: line.rows.filter((_, j) => j !== ri) },
            ),
        )
    }

    const boxClass = compact ? 'rounded-lg border p-2' : 'rounded-lg border p-3'
    const lineKey = (line: LotLine) => line.orderItemId || line.productId

    return (
        <div className={compact ? 'min-w-0 space-y-3' : 'min-w-0 space-y-5'}>
            {lines.map((line, li) => {
                const mismatch = lineMismatch(line, allowShortfall)
                const shortfall = allowShortfall ? lineShortfall(line) : 0
                return (
                    <div key={lineKey(line)} className={boxClass}>
                        <div className="mb-2 flex items-baseline justify-between gap-2">
                            <span className="font-medium">
                                {line.productName}
                            </span>
                            <span className="text-sm text-muted-foreground">
                                {m.stock_out_dialog_line_needed({
                                    qty: formatQty(line.quantity),
                                    unit: line.unit,
                                })}
                            </span>
                        </div>

                        <div className="space-y-2">
                            {line.rows.map((row, ri) => {
                                const err =
                                    allowShortfall && isBlankRow(row)
                                        ? null
                                        : rowError(row, line.unit)
                                return (
                                    <div key={ri}>
                                        <div className="flex items-center gap-2">
                                            <div className="min-w-0 flex-1">
                                                <OrderLotPicker
                                                    workflow={workflow}
                                                    productId={line.productId}
                                                    warehouseId={warehouseId}
                                                    value={row.lotId}
                                                    seedLots={
                                                        row.lot
                                                            ? [row.lot]
                                                            : undefined
                                                    }
                                                    onChange={(lot) =>
                                                        patchRow(li, ri, {
                                                            lotId: lot.id,
                                                            lot,
                                                        })
                                                    }
                                                />
                                            </div>
                                            <Input
                                                type="number"
                                                className="w-24"
                                                aria-label={`qty-${lineKey(line)}-${ri}`}
                                                value={row.qty}
                                                onChange={(e) =>
                                                    patchRow(li, ri, {
                                                        qty: e.target.value,
                                                    })
                                                }
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                disabled={line.rows.length <= 1}
                                                onClick={() =>
                                                    removeRow(li, ri)
                                                }
                                            >
                                                {m.stock_out_dialog_remove_lot()}
                                            </Button>
                                        </div>
                                        {row.lot && (
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                {lotInfoLine(
                                                    row.lot,
                                                    line.unit,
                                                )}
                                            </p>
                                        )}
                                        {err && (
                                            <p className="mt-1 text-xs text-destructive">
                                                {err}
                                            </p>
                                        )}
                                    </div>
                                )
                            })}
                        </div>

                        <div className="mt-2 flex items-center justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => addRow(li)}
                            >
                                {m.stock_out_dialog_add_lot()}
                            </Button>
                            <span
                                className={
                                    mismatch
                                        ? 'text-xs text-destructive'
                                        : shortfall > 0
                                          ? 'text-xs font-medium text-amber-700 dark:text-amber-500'
                                          : 'text-xs text-muted-foreground'
                                }
                            >
                                {shortfall > 0
                                    ? m.pos_lot_debt_shortfall({
                                          qty: formatQty(shortfall),
                                          unit: line.unit,
                                      })
                                    : m.stock_out_dialog_allocated({
                                          allocated: formatQty(
                                              allocatedOf(line),
                                          ),
                                          total: formatQty(line.quantity),
                                          unit: line.unit,
                                      })}
                            </span>
                        </div>
                    </div>
                )
            })}

            {matLines.length > 0 && (
                <div className="space-y-3">
                    <div className="text-sm font-semibold">
                        {m.stock_out_material_section_title()}
                    </div>
                    {matLines.map((line, li) => {
                        const mismatch = matMismatch(line)
                        const isQty = line.trackingMode === 'quantity'
                        return (
                            <div key={line.materialId} className={boxClass}>
                                <div className="mb-2 flex items-baseline justify-between gap-2">
                                    <span className="font-medium">
                                        {line.name}
                                    </span>
                                    <span className="text-sm text-muted-foreground">
                                        {m.stock_out_dialog_line_needed({
                                            qty: formatQty(line.needed),
                                            unit: line.unit,
                                        })}
                                    </span>
                                </div>
                                {isQty ? (
                                    <>
                                        <p className="text-sm text-muted-foreground">
                                            {m.stock_out_material_qty_mode()}
                                        </p>
                                        {mismatch && (
                                            <p className="mt-1 text-xs text-destructive">
                                                {m.stock_out_material_no_stock()}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="space-y-2">
                                            {line.rows.map((row, ri) => {
                                                const err = matRowError(
                                                    row,
                                                    line.unit,
                                                )
                                                return (
                                                    <div key={ri}>
                                                        <div className="flex items-center gap-2">
                                                            <div className="min-w-0 flex-1">
                                                                <MaterialLotPicker
                                                                    materialId={
                                                                        line.materialId
                                                                    }
                                                                    value={
                                                                        row.lotId
                                                                    }
                                                                    seedLot={
                                                                        row.lot
                                                                    }
                                                                    excludeLotIds={line.rows
                                                                        .filter(
                                                                            (
                                                                                _,
                                                                                j,
                                                                            ) =>
                                                                                j !==
                                                                                ri,
                                                                        )
                                                                        .map(
                                                                            (
                                                                                r2,
                                                                            ) =>
                                                                                r2.lotId,
                                                                        )
                                                                        .filter(
                                                                            Boolean,
                                                                        )}
                                                                    onChange={(
                                                                        lot,
                                                                    ) =>
                                                                        patchMatRow(
                                                                            li,
                                                                            ri,
                                                                            {
                                                                                lotId: lot.id,
                                                                                lot,
                                                                            },
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                            <Input
                                                                type="number"
                                                                className="w-24"
                                                                aria-label={`mat-qty-${line.materialId}-${ri}`}
                                                                value={row.qty}
                                                                onChange={(e) =>
                                                                    patchMatRow(
                                                                        li,
                                                                        ri,
                                                                        {
                                                                            qty: e
                                                                                .target
                                                                                .value,
                                                                        },
                                                                    )
                                                                }
                                                            />
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                disabled={
                                                                    line.rows
                                                                        .length <=
                                                                    1
                                                                }
                                                                onClick={() =>
                                                                    removeMatRow(
                                                                        li,
                                                                        ri,
                                                                    )
                                                                }
                                                            >
                                                                {m.stock_out_dialog_remove_lot()}
                                                            </Button>
                                                        </div>
                                                        {row.lot && (
                                                            <p className="mt-1 text-xs text-muted-foreground">
                                                                {m.stock_out_material_lot_info(
                                                                    {
                                                                        code: row
                                                                            .lot
                                                                            .code,
                                                                        available:
                                                                            formatQty(
                                                                                row
                                                                                    .lot
                                                                                    .available,
                                                                            ),
                                                                        unit: line.unit,
                                                                    },
                                                                )}
                                                            </p>
                                                        )}
                                                        {err && (
                                                            <p className="mt-1 text-xs text-destructive">
                                                                {err}
                                                            </p>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                        <div className="mt-2 flex items-center justify-between">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => addMatRow(li)}
                                            >
                                                {m.stock_out_dialog_add_lot()}
                                            </Button>
                                            <span
                                                className={
                                                    mismatch
                                                        ? 'text-xs text-destructive'
                                                        : 'text-xs text-muted-foreground'
                                                }
                                            >
                                                {m.stock_out_dialog_allocated({
                                                    allocated: formatQty(
                                                        matAllocated(line),
                                                    ),
                                                    total: formatQty(
                                                        line.needed,
                                                    ),
                                                    unit: line.unit,
                                                })}
                                            </span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
